import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { listRouteOptions } from "../../../api/threads/routeOptions";
import type { ThreadStepPlan } from "../../../api/threads/threadsApi";
import { changeThreadSettings } from "../data/settingsChanges";
import { useSubmission } from "../../../shared/state/useSubmission";
import ConnectorSetupButton from "../../connectors/setup/ConnectorSetupButton";
import { optionOf, settingsWithChoice, type TurnRoute } from "../hosting/routeChoices";
import { useThreadContext } from "../page/threadPageContext";
import { allows } from "../page/viewerActions";
import type { LlmChoice } from "./LlmPicker";
import { offeredOn, picksLlm, withoutEffort } from "./llmOptions";
import RunsWithRow from "./RunsWithRow";
import { settingsWithStepRoute, stepsWithOwnRoute } from "./stepRoutes";
import { useLlmChoices } from "./useLlmChoices";
import { useRunsWithOpener } from "./runsWithOpener";
import LoadStatus from "../../../ui/feedback/LoadStatus";

/**
 * "Runs with" for a Thread (W-07, F106 Design C): its default once, in the header and wherever
 * the composer or a problem asks for an LLM; on an agent step only where the step has its own.
 * The host changes it in the one LLM picker, which writes the Thread's settings: `route`, or a
 * step's `step_routes`. "Runs on" (the hosting line) says only where.
 */
/** The Thread's default row, once, under the header: the one place it is chosen (S12, DR-C-02). */
export function ThreadDefaultRunsWith() {
  const { t } = useTranslation();
  const { threadId, overview } = useThreadContext();
  // Module C's decision (W-22 slice 4): the route is the Run's settings, the host's.
  const choosing = allows(overview.viewer, "change_settings");
  const opener = useRunsWithOpener();
  const settings = overview.settings;
  // Live, its PC's options; else every online PC's, so a saved Thread's host may choose ahead.
  const connectorId = overview.state === "live" ? overview.attachedConnector : null;
  const optionsOf = useCallback((id: string, signal: AbortSignal) => listRouteOptions(threadId, id, signal), [threadId]);
  const { state, refresh, reload } = useLlmChoices(!choosing ? [] : connectorId === null ? "online" : [connectorId], optionsOf);
  // The server's answer for the default leaves out the steps with their own LLM: asked again
  // whenever they change (Sol R2 S7), as the hosting line reads again when hosting changes.
  const stepRoutes = JSON.stringify(settings.step_routes);
  const readFor = useRef(stepRoutes);
  useEffect(() => {
    if (readFor.current === stepRoutes) return;
    readFor.current = stepRoutes;
    refresh();
  }, [stepRoutes, refresh]);
  const choices = state.status === "loaded" ? state.value : [];
  // Saved, the route is judged on a PC that offers it, never on whichever PC came first (Sol R1 S3).
  const route = settings.route;
  const shownOn = connectorId ?? (route === null ? undefined : choices.find((choice) => offeredOn(route, choice))?.connector.connectorId) ?? choices[0]?.connector.connectorId ?? null;
  const { state: saving, submit } = useSubmission();
  const differ = stepsWithOwnRoute(settings);
  const choose = (choice: LlmChoice) => {
    const listing = choices.find((candidate) => candidate.connector.connectorId === choice.connectorId)?.listing;
    const drops = optionOf(listing?.options ?? null, withoutEffort(choice.route))?.dropsDefaultReplyTools ?? false;
    const changed = settingsWithChoice(settings, { connectorId: choice.connectorId, route: choice.route, permissionProfile: settings.permission_profile, fallbackRoutes: settings.fallback_routes ?? [], dropsDefaultReplyTools: drops });
    if (changed === null) return;
    submit(async () => {
      // The owner of settings writes, which sends once more if the log moved on (Sol R3 S8).
      await changeThreadSettings(threadId, overview.lastEntryNumber, changed, overview.bindings);
    });
  };

  if (state.status === "failed") return <LoadStatus state={state} loadingLabel={t("threads.llm.loading")} onRetry={reload} compact />;
  return (
    <RunsWithRow
      route={route}
      connectorId={shownOn}
      note={differ === 0 ? null : t("threads.header.stepsDiffer", { count: differ })}
      choices={choices}
      // Only the host's PCs answer route options: for anyone else nothing is read, so nothing is judged (Sol R1 S2).
      read={choosing && state.status === "loaded"}
      onRefresh={refresh}
      mayPick={choosing}
      own={false}
      onChoose={(choice) => { if (choice !== null) choose(choice); }}
      noChoices={<ConnectorSetupButton inline={true} />}
      opener={opener}
      busy={saving.status === "submitting"}
      error={saving.status === "failed" ? saving.error : null}
    />
  );
}

/** A step's own LLM, where it has one; for the host, the way to give it one. */
export default function RunsWithCard({ plan }: { plan: ThreadStepPlan }) {
  if (plan.llmRequirements === null || plan.runsWith === null) return null;
  return <ThreadRunsWith stepKey={plan.stepKey} route={plan.runsWith.route} own={plan.runsWith.own} picks={picksLlm(plan.llmRequirements)} />;
}

function ThreadRunsWith({ stepKey, route, own, picks }: { stepKey: string; route: TurnRoute | null; own: boolean; picks: boolean }) {
  const { t } = useTranslation();
  const { threadId, overview } = useThreadContext();
  // Module C's decision (W-22 slice 4): the route is the Run's settings, the host's.
  const choosing = allows(overview.viewer, "change_settings");
  // Only the host's own PC answers route options for them; a step runs where the Thread runs.
  const connectorId = choosing ? overview.attachedConnector : null;
  const optionsOf = useCallback((id: string, signal: AbortSignal) => listRouteOptions(threadId, id, signal, stepKey), [threadId, stepKey]);
  const { state, refresh, reload } = useLlmChoices(connectorId === null ? [] : [connectorId], optionsOf);
  const { state: saving, submit } = useSubmission();
  const choose = (chosen: TurnRoute | null) => {
    submit(async () => {
      await changeThreadSettings(threadId, overview.lastEntryNumber, settingsWithStepRoute(overview.settings, stepKey, chosen), overview.bindings);
    });
  };
  // A failed read is said with Retry (Sol S13); the default is said once, in the header.
  if (state.status === "failed") return <LoadStatus state={state} loadingLabel={t("threads.llm.loading")} onRetry={reload} compact />;
  return (
    <RunsWithRow
      route={route}
      connectorId={connectorId}
      note={null}
      choices={state.status === "loaded" ? state.value : []}
      // Only the host's PC answers; for anyone else nothing is read, so nothing is judged (Sol R3 S9).
      read={choosing && state.status === "loaded"}
      onRefresh={refresh}
      mayPick={picks && choosing}
      own={own}
      askLabel={t("threads.llm.ownForStep")}
      onChoose={(choice) => { choose(choice?.route ?? null); }}
      busy={saving.status === "submitting"}
      error={saving.status === "failed" ? saving.error : null}
    />
  );
}
